import { inventoryIdentifier } from "./inventoryItemFields";
import { getGroupAssignmentOutcome, isInventoryActionBlocked, isInventoryReassignAllowed } from "./inventoryAssignment";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Search, Store, Users, AlertTriangle, ArrowRight, ArrowDown } from "lucide-react";
import { UploadDropZone } from "./inventoryUploadShared";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import { isRTL } from "../../utils/rtl";
import { toast } from "../../utils/toast";
import CommonButton from "../common/CommonButton";
import ModalCloseButton from "../common/ModalCloseButton";
import ReactSelector from "../common/CommonReactSelector";
import {
  GroupSelectList,
  groupMatchesQuery,
  filterGroupsByUserAccess,
  normalizeGroups,
  isGroupAssociatedWithProject,
} from "../task/GroupAssignParts";
import { getInventoryOrganizationIds, loadInventoryGroups } from "./inventoryGroupScope";
import {
  fetchInventoryItems,
  assignInventoryMeterNumbers,
  reassignInventoryMeterNumbers,
  assignInventoryExcel,
} from "../../service/MeterInventoryManagement";
import {
  getSkippedMeters,
  getMeterTransferCounts,
  hasMeterTransferResult,
  downloadSkippedMetersExcel,
} from "../../utils/skippedMetersExport";
import {
  FormField,
  InlineSpinner,
  MODAL_FOOTER_BUTTON_CLASS,
  OptionCard,
  apiErrorMessage,
  apiSuccessMessage,
  itemTypeOptionsFor,
  useInventoryScope,
  useInventoryStores,
} from "./inventoryFormShared";

export const InventoryTransferModal = ({ open, onClose, onTransferred, selectedItems = [], mode = "transfer" }) => {
  const isReassign = mode === "reassign";
  const hasBlockedItems = selectedItems.some((item) => isReassign ? !isInventoryReassignAllowed(item) : isInventoryActionBlocked(item));
  const { theme, langMode, departmentId } = useContext(AppContext);
  const isRtl = isRTL(langMode);
  const t = translations[langMode] || translations.en;
  const itemTypeOptions = itemTypeOptionsFor(t);

  const { projectId, organizationId } = useInventoryScope();
  const { storeOptions, loading: storesLoading } = useInventoryStores(open, projectId, langMode);

  const [transferTarget, setTarget] = useState("store");
  const target = transferTarget;
  const [storeId, setStoreId] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [loadedGroupScope, setLoadedGroupScope] = useState(null);
  const [inputMode, setInputMode] = useState("selected");
  const sourceOrgIds = getInventoryOrganizationIds(inputMode === "selected" ? selectedItems : []);
  // Both input modes use the header scope, matching the Meter numbers group list.
  const assignmentOrgId = Number(organizationId);
  const groupScope = JSON.stringify([assignmentOrgId, departmentId]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupQuery, setGroupQuery] = useState("");
  const [meterText, setMeterText] = useState("");
  const [manualAssignment, setManualAssignment] = useState(null);
  const manualLookupRef = useRef(null);
  const manualNumbersKey = JSON.stringify([...new Set(meterText.split(/[\s,;]+/).filter(Boolean))]);
  const manualScopeKey = JSON.stringify([manualNumbersKey, organizationId, projectId]);
  const assignmentItems = inputMode === "selected" ? selectedItems :
    inputMode === "manual" && manualAssignment?.key === manualScopeKey ? manualAssignment.items : [];
  // Only preselect when every entered/selected meter has the same assignment.
  const assignedGroupIds = assignmentItems.map((item) => {
    const raw = item.raw || item;
    const id = raw.groupAssignmentDetails?.groupId ?? raw.groupId ?? item.groupId;
    return id == null || id === "" ? null : String(id);
  });
  const assignedGroupId = assignedGroupIds.length > 0 &&
    assignedGroupIds[0] && assignedGroupIds.every((id) => id === assignedGroupIds[0])
    ? assignedGroupIds[0] : null;
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const selectTransferFile = (nextFile) => {
    if (saving || !nextFile) return;
    if (!/\.xlsx?$/i.test(nextFile.name)) {
      toast.warn(t.inventoryAssignmentFileRequired);
      return;
    }
    setFile(nextFile);
    setTransferError("");
  };
  const [itemType, setItemType] = useState("NEW_METER");
  const [transferError, setTransferError] = useState("");

  useEffect(() => {
    if (!open) {
      setTarget("store");
      setStoreId("");
      setSelectedGroupId(null);
      setGroupQuery("");
      setInputMode("selected");
      setMeterText("");
      setFile(null);
      setItemType("NEW_METER");
      setSaving(false);
      setTransferError("");
    }
  }, [open]);

  useEffect(() => {
    setManualAssignment(null);
    // The lookup only preselects the meters' current group, so store transfers skip it.
    if (!open || inputMode !== "manual" || target !== "group") return;
    setSelectedGroupId(null);
    const numbers = JSON.parse(manualNumbersKey);
    if (!numbers.length || !organizationId || !projectId) return;
    const controller = new AbortController();
    manualLookupRef.current = controller;
    const timer = setTimeout(async () => {
      try {
        const items = [];
        let page = 0;
        const size = 100;
        while (!controller.signal.aborted) {
          const data = await fetchInventoryItems({
            organizationId: Number(organizationId), projectIds: [Number(projectId)], page, size,
            columnFilters: [{ column: "identifier", op: "in", values: numbers }],
          }, { signal: controller.signal });
          const rows = Array.isArray(data) ? data :
            data?.itemList ?? data?.meterInventoryList ?? data?.content ?? data?.items ?? data?.data ?? [];
          if (!Array.isArray(rows)) return;
          items.push(...rows);
          page += 1;
          if (Array.isArray(data) || !rows.length ||
            (data?.totalPages != null ? page >= data.totalPages : rows.length < size)) break;
        }
        if (controller.signal.aborted) return;
        const matches = items.filter((item) => numbers.includes(String(inventoryIdentifier(item) ?? "").trim()));
        const complete = numbers.every((number) => matches.some((item) => String(inventoryIdentifier(item) ?? "").trim() === number));
        setManualAssignment({ key: manualScopeKey, items: complete ? matches : [] });
      } catch {
        // Assignment lookup must not prevent the existing manual transfer flow.
        if (!controller.signal.aborted) setManualAssignment(null);
      }
    }, 350);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, inputMode, target, manualNumbersKey, manualScopeKey, organizationId, projectId]);

  // Reload on every scope change and reopening; reject departments from another organization.
  useEffect(() => {
    setGroups([]);
    setSelectedGroupId(null);
    setLoadedGroupScope(null);
    if (!open || target !== "group") return;
    let cancelled = false;
    const load = async () => {
      setGroupsLoading(true);
      try {
        const raw = await loadInventoryGroups(assignmentOrgId, departmentId);
        if (!cancelled) {
          const availableGroups = normalizeGroups(filterGroupsByUserAccess(raw));
          setGroups(availableGroups);
          setSelectedGroupId(availableGroups.find((group) => String(group.groupId) === assignedGroupId)?.groupId ?? null);
          setLoadedGroupScope(groupScope);
        }
      } catch {
        if (!cancelled) {
          setGroups([]);
          toast.error(t.inventoryTransferGroupLoadFailed);
        }
      } finally {
        if (!cancelled) setGroupsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target, assignmentOrgId, departmentId, assignedGroupId]);

  const filteredGroups = useMemo(
    () => loadedGroupScope === groupScope
      ? groups.filter((group) => groupMatchesQuery(group, groupQuery)) : [],
    [groups, groupQuery, loadedGroupScope, groupScope]
  );

  const currentStoreOptions = useMemo(() => {
    const assigned = new Map();
    selectedItems.forEach((item) => {
      const raw = item.raw || item;
      const id = item.storeId ?? raw.storeId ?? raw.store?.id ?? raw.store?.storeId;
      const name = raw.storeName ?? raw.store?.storeName ?? raw.store?.name;
      const value = id == null || id === "" ? (name || "unassigned") : String(id);
      const label = name || storeOptions.find((option) => String(option.value) === value)?.label ||
        (value === "unassigned" ? t.inventoryTransferUnassignedStore : `${t.inventoryStoreId} ${value}`);
      assigned.set(value, { value, label });
    });
    return [...assigned.values()];
  }, [selectedItems, storeOptions, t]);

  const sourceProjectIds = useMemo(
    () => [...new Set(selectedItems.map((item) => item.projectId).filter(Boolean).map(Number))],
    [selectedItems]
  );
  const effectiveProjectId = useMemo(
    () => Number(inputMode === "selected" ? sourceProjectIds[0] || projectId : projectId),
    [inputMode, sourceProjectIds, projectId]
  );

  const candidateProjectIds = useMemo(() => {
    const list = [];
    const add = (v) => {
      if (v !== undefined && v !== null && String(v).trim() !== "" && String(v) !== "NaN") {
        list.push(String(v).trim());
      }
    };
    add(effectiveProjectId);
    add(projectId);
    sourceProjectIds.forEach(add);
    selectedItems.forEach((item) => {
      add(item.projectId);
      add(item.raw?.projectId);
      add(item.raw?.project_id);
      add(item.raw?.project?.id);
      add(item.raw?.project?.name);
    });
    return [...new Set(list)];
  }, [effectiveProjectId, projectId, sourceProjectIds, selectedItems]);

  const selectedGroup = useMemo(
    () => groups.find((group) => group.groupId === selectedGroupId) || null,
    [groups, selectedGroupId]
  );

  useEffect(() => {
    if (
      selectedGroupId &&
      selectedGroup &&
      candidateProjectIds.length > 0 &&
      !isGroupAssociatedWithProject(selectedGroup, candidateProjectIds)
    ) {
      setSelectedGroupId(null);
    }
  }, [selectedGroupId, selectedGroup, candidateProjectIds]);

  const handleTransfer = async () => {
    if (saving || hasBlockedItems) return;
    if (inputMode === "selected" && selectedItems.length === 0) {
      toast.warn(t.inventoryTransferNoSelection);
      return;
    }
    if (target === "store" && (storesLoading || !storeOptions.some((option) => option.value === storeId))) {
      toast.warn(t.inventoryTransferSelectStoreFirst);
      return;
    }
    if (target === "group" && (groupsLoading || loadedGroupScope !== groupScope || !selectedGroup)) {
      toast.warn(t.inventoryTransferSelectGroupFirst);
      return;
    }
    if (target === "group" && selectedGroup && candidateProjectIds.length > 0 && !isGroupAssociatedWithProject(selectedGroup, candidateProjectIds)) {
      toast.warn(t.groupNotInProject ?? "Selected group does not belong to this project");
      return;
    }

    const inventoryItemIds = selectedItems.map((item) => item.id);
    const selectedNumbers = selectedItems.map((item) =>
      inventoryIdentifier(item.raw) ?? inventoryIdentifier(item)
    );
    const meterNos = inputMode === "manual"
      ? [...new Set(meterText.split(/[\s,;]+/).filter(Boolean))]
      : [...new Set(selectedNumbers.map((value) => String(value ?? "").trim()).filter(Boolean))];
    if (inputMode !== "excel" && (!meterNos.length || (inputMode === "selected" && selectedNumbers.some((value) => value == null || !String(value).trim())))) {
      toast.warn(t.inventoryMeterNumbersRequired);
      return;
    }
    if (inputMode === "excel" && (!file || !/\.xlsx?$/i.test(file.name))) {
      toast.warn(t.inventoryAssignmentFileRequired);
      return;
    }
    if (!Number.isSafeInteger(effectiveProjectId) || effectiveProjectId <= 0 ||
      (inputMode === "selected" && (sourceProjectIds.length > 1 || sourceOrgIds.length > 1))) {
      toast.warn(t.inventoryAssignmentProjectRequired);
      return;
    }
    const destination = target === "store" ? { toStoreIds: [Number(storeId)] }
      : target === "group" ? { groupIds: [Number(selectedGroupId)] } : {};

    // Don't let a pending group lookup run alongside the assignment request.
    manualLookupRef.current?.abort();
    setTransferError("");
    setSaving(true);
    // meter-nos reports per-meter counts plus a `skipped` list; the skipped meters are
    // downloaded as a Meter No / Reason sheet and the toast states all three counts.
    const summarizeMeterResult = (data, fallback) => {
      const counts = getMeterTransferCounts(data, fallback);
      const skippedMeters = getSkippedMeters(data);
      if (skippedMeters.length > 0) downloadSkippedMetersExcel(skippedMeters);
      const summary = t.inventoryMeterTransferSummary
        .replace("{success}", counts.transferred)
        .replace("{skipped}", counts.skipped);
      return {
        counts,
        message: skippedMeters.length > 0 ? `${summary} ${t.inventorySkippedMetersDownloaded}` : summary,
      };
    };
    try {
      const response = inputMode === "excel"
        ? await assignInventoryExcel({ file, projectId: effectiveProjectId, organizationId, itemType, ...destination })
        : await (isReassign ? reassignInventoryMeterNumbers : assignInventoryMeterNumbers)({
            projectId: effectiveProjectId, meterNos, ...destination,
          });
      {
        const outcome = getGroupAssignmentOutcome(response, inventoryItemIds, selectedItems);
        if (outcome.pending) {
          toast.info(apiSuccessMessage(response, t.Processing));
          // The backend is still applying the assignment, so a list reload here
          // would only return stale rows.
          onTransferred?.(inventoryItemIds, { reload: false });
          onClose?.();
          return;
        }
        if (hasMeterTransferResult(response)) {
          const { counts, message } = summarizeMeterResult(response, {
            transferred: outcome.assignedCount,
            failed: outcome.failedCount,
          });
          // The request has finished: report the counts in a toast and close. Skipped
          // meters are already in the downloaded sheet, and failed rows stay selected.
          if (counts.failed > 0 || counts.skipped > 0) {
            if (counts.transferred > 0) toast.warn(message);
            else toast.error(message);
            onTransferred?.(inputMode === "selected" ? outcome.failedIds : inventoryItemIds);
            onClose?.();
            return;
          }
          toast.success(message);
          onTransferred?.();
          onClose?.();
          return;
        }
        if (outcome.failedCount > 0) {
          const summary = t.inventoryTransferAssignmentResult
            .replace("{assigned}", outcome.assignedCount)
            .replace("{failed}", outcome.failedCount);
          const message = outcome.reason
            ? (outcome.assignedCount > 0 ? `${summary} — ${outcome.reason}` : outcome.reason)
            : summary;
          if (outcome.assignedCount > 0) toast.warn(message);
          else toast.error(message);
          onTransferred?.(inputMode === "selected" ? outcome.failedIds : inventoryItemIds);
          onClose?.();
          return;
        }
      }
      toast.success(apiSuccessMessage(response, t.inventoryTransferSuccess));
      onTransferred?.();
      onClose?.();
    } catch (error) {
      // When every meter is refused the backend reports it as a failure, but the
      // counts and skipped list still come back on the error payload.
      if (hasMeterTransferResult(error?.response?.data)) {
        const { message } = summarizeMeterResult(error.response.data);
        toast.error(message);
        onTransferred?.(inputMode === "selected" ? inventoryItemIds : []);
        onClose?.();
        return;
      }
      // A request-level error (no result came back) keeps the modal open so the
      // user can correct the input and retry.
      const errorMsg = apiErrorMessage(error, t.inventoryTransferFailed);
      setTransferError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Warn before a refresh or tab close drops a long-running assign/reassign request.
  useEffect(() => {
    if (!saving) return undefined;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [saving]);

  // The modal stays locked until the assign/reassign request settles.
  const handleClose = () => {
    if (!saving) onClose?.();
  };

  if (!open) return null;

  return (
    <div
      className={`fixed inset-0 z-[1300] flex items-center justify-center p-4 transition-colors ${
        saving ? "cursor-wait bg-slate-900/60 backdrop-blur-[2px]" : "bg-slate-900/40 backdrop-blur-[1px]"
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="inventory-transfer-modal-title"
      aria-busy={saving}
      data-testid="inventory-transfer-modal"
      dir={isRtl ? "rtl" : "ltr"}
    >
      <div
        className={`relative flex max-h-[90dvh] w-full max-w-[720px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ${
          theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""
        }`}
      >
        {/* assign/reassign meter-nos can run long; cover the whole card so the user
            sees the request is still running and nothing inside can be used. */}
        {saving && (
          <div
            className={`absolute inset-0 z-30 flex items-center justify-center ${
              theme === "high-contrast" ? "bg-black/80" : theme === "dark" ? "bg-slate-900/75" : "bg-white/75"
            }`}
            data-testid="inventory-transfer-in-progress"
          >
            <div
              className={`flex max-w-[320px] flex-col items-center gap-3 px-6 text-center text-sm ${
                theme === "high-contrast" ? "text-yellow-300" : theme === "dark" ? "text-slate-100" : "text-slate-600"
              }`}
            >
              <InlineSpinner size={36} />
              <p className="font-semibold">{t.inventoryTransferring}</p>
              <p className="text-xs opacity-80">{t.inventoryTransferInProgressHint}</p>
            </div>
          </div>
        )}
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
          <div>
            <h2
              id="inventory-transfer-modal-title"
              className="text-base font-semibold text-slate-800"
            >
              {isReassign ? t.inventoryReassign : t.inventoryTransferTitle}
            </h2>
            <p className="mt-1 text-sm text-slate-500">{t.inventoryTransferSubtitle}</p>
          </div>
          <ModalCloseButton
            onClick={handleClose}
            disabled={saving}
            className="border-0 bg-transparent shadow-none hover:border-0"
            ariaLabel={t.cancelandclose}
          />
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
          {transferError && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700 flex items-start gap-2.5 shadow-sm"
              data-testid="inventory-transfer-error-banner"
            >
              <AlertTriangle size={15} className="shrink-0 text-red-500 mt-0.5" />
              <div className="flex-1 min-w-0 font-medium break-words leading-relaxed">{transferError}</div>
            </div>
          )}

          <fieldset disabled={saving} className="min-w-0 space-y-3">
            <FormField label={t.inventoryAssignmentInput}>
              <ReactSelector
                options={[
                  { value: "selected", label: t.inventoryTransferSelectedItems },
                  { value: "manual", label: t.inventoryMeterNumbers },
                  ...(!isReassign ? [{ value: "excel", label: t.inventoryAssignmentExcel }] : []),
                ]}
                value={{ value: inputMode, label: inputMode === "selected" ? t.inventoryTransferSelectedItems : inputMode === "manual" ? t.inventoryMeterNumbers : t.inventoryAssignmentExcel }}
                onChange={(option) => {
                  setInputMode(option.value);
                  setTransferError("");
                }}
                isDisabled={saving}
              />
            </FormField>
            {inputMode === "manual" && <FormField label={t.inventoryMeterNumbers} required>
              <textarea value={meterText} onChange={(event) => { setMeterText(event.target.value); setTransferError(""); }} rows={4}
                placeholder={t.inventoryMeterNumbersHint} className="w-full rounded-lg border border-slate-200 p-3 text-sm" />
            </FormField>}
            {inputMode === "excel" && <>
              <FormField label={t.inventoryAssignmentExcel} required>
                <UploadDropZone
                  accept=".xlsx,.xls"
                  disabled={saving}
                  showColumnCount={false}
                  t={{ ...t, inventoryImportDropTitle: t.dragandroptoupload, inventoryImportDropHint: ".xlsx / .xls" }}
                  upload={{
                    fileInputRef,
                    pendingFile: file,
                    isDraggingFile,
                    setIsDraggingFile,
                    selectFile: selectTransferFile,
                    openFilePicker: () => fileInputRef.current?.click(),
                    handleFileDrop: (event) => {
                      setIsDraggingFile(false);
                      selectTransferFile(event.dataTransfer.files?.[0]);
                    },
                  }}
                />
              </FormField>
              <FormField label={t.inventoryItemType} required>
                <ReactSelector options={itemTypeOptions}
                  value={itemTypeOptions.find((option) => option.value === itemType) || null}
                  onChange={(option) => { setItemType(option.value); setTransferError(""); }} isDisabled={saving} />
              </FormField>
            </>}
          </fieldset>
          {inputMode === "selected" && <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              {t.inventoryTransferSelectedItems}
            </p>
            <p className="mt-1 text-sm font-medium text-slate-700">
              {(t.inventoryTransferSelectedCount || "").replace("{count}", selectedItems.length)}
            </p>
            {selectedItems.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {selectedItems.slice(0, 12).map((item) => (
                  <span
                    key={item.id ?? item.identifier}
                    className="max-w-full break-all rounded-full bg-white px-2.5 py-1 text-xs text-slate-600 ring-1 ring-slate-200"
                  >
                    {item.identifier ?? item.id}
                  </span>
                ))}
                {selectedItems.length > 12 && (
                  <span className="rounded-full bg-white px-2.5 py-1 text-xs text-slate-400 ring-1 ring-slate-200">
                    +{selectedItems.length - 12}
                  </span>
                )}
              </div>
            )}
          </div>}

          <div>
            <p className="text-sm font-medium text-[#374151]">{t.inventoryTransferTargetTitle}</p>
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <OptionCard
                name="inventory-transfer-target"
                checked={target === "store"}
                disabled={saving}
                onChange={() => {
                  setTarget("store");
                  setTransferError("");
                }}
                label={t.inventoryTransferTargetStore}
                hint={t.inventoryTransferTargetStoreHint}
                testId="inventory-transfer-target-store"
                icon={Store}
              />
              <OptionCard
                name="inventory-transfer-target"
                checked={target === "group"}
                disabled={saving}
                onChange={() => {
                  setTarget("group");
                  setTransferError("");
                }}
                label={t.inventoryTransferTargetGroup}
                hint={t.inventoryTransferTargetGroupHint}
                testId="inventory-transfer-target-group"
                icon={Users}
              />
              {isReassign && <OptionCard name="inventory-transfer-target" checked={target === "clear"}
                disabled={saving}
                onChange={() => {
                  setTarget("clear");
                  setTransferError("");
                }} label={t.inventoryClearCustody}
                hint={t.inventoryClearCustodyHint} testId="inventory-transfer-target-clear" />}
            </div>
          </div>

          <div className={target === "store" ? "grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-end" : "min-w-0"}>
            {target === "store" && <FormField label={t.inventoryTransferCurrentStore} className="min-w-0">
              <ReactSelector
                options={currentStoreOptions}
                value={inputMode === "selected" ? (currentStoreOptions.length > 1 ? currentStoreOptions : currentStoreOptions[0] || null) : null}
                isMulti={inputMode === "selected" && currentStoreOptions.length > 1}
                isDisabled
                placeholder={inputMode === "selected" ? t.notAvailable : t.inventoryTransferCurrentStoreUnknown}
                aria-label={t.inventoryTransferCurrentStore}
                dataTestId="inventory-transfer-current-store"
                height="40px"
              />
            </FormField>}
            {target === "store" && (
              <div
                aria-hidden="true"
                className={`flex h-8 w-8 items-center justify-center justify-self-center rounded-full sm:mb-1 ${
                  theme === "high-contrast" ? "bg-black text-yellow-300 ring-1 ring-yellow-300" :
                  theme === "dark" ? "bg-slate-800 text-blue-300" : "bg-blue-50 text-[#236DB4]"
                }`}
              >
                <ArrowDown size={18} className="sm:hidden" />
                <ArrowRight size={18} className={`hidden sm:block ${isRtl ? "rotate-180" : ""}`} />
              </div>
            )}
          {target === "store" ? (
            <FormField label={t.inventoryTransferNewStore} className="min-w-0" required>
              <ReactSelector
                options={storeOptions}
                value={storeOptions.find((option) => option.value === storeId) || null}
                onChange={(option) => {
                  setStoreId(option?.value ?? "");
                  setTransferError("");
                }}
                placeholder={
                  storesLoading
                    ? t.Processing
                    : storeOptions.length === 0
                      ? t.inventoryImportStoresEmpty
                      : t.inventoryImportSelectStorePlaceholder
                }
                isDisabled={saving || storesLoading || storeOptions.length === 0}
                isSearchable
                height="40px"
                borderRadius="8px"
                fontSize="14px"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                dataTestId="inventory-transfer-store-select"
              />
            </FormField>
          ) : target === "group" ? (
            <div>
              <div className="relative">
                <Search
                  size={15}
                  className={`absolute top-1/2 -translate-y-1/2 text-slate-400 ${
                    isRtl ? "right-3" : "left-3"
                  }`}
                />
                <input
                  type="text"
                  value={groupQuery}
                  onChange={(event) => setGroupQuery(event.target.value)}
                  disabled={saving}
                  placeholder={t.inventoryTransferSearchGroup}
                  data-testid="inventory-transfer-group-search"
                  className={`h-10 w-full rounded-lg border border-slate-200 text-sm outline-none transition focus:border-[#236DB4] focus:ring-2 focus:ring-[#236DB4]/20 ${
                    isRtl ? "pr-9 pl-3" : "pl-9 pr-3"
                  }`}
                />
              </div>
              <div
                aria-disabled={saving}
                className={`mt-3 max-h-[280px] overflow-y-auto rounded-xl border border-slate-200 p-2 ${
                  saving ? "pointer-events-none opacity-60" : ""
                }`}
              >
                {groupsLoading ? (
                  <div className="flex items-center justify-center gap-2 py-6 text-sm text-slate-400">
                    <InlineSpinner size={16} />
                    {t.Processing}
                  </div>
                ) : (
                  <GroupSelectList
                    groups={filteredGroups}
                    selectedGroupId={selectedGroupId}
                    onSelectGroup={(id) => {
                      if (!saving) {
                        setSelectedGroupId(id);
                        setTransferError("");
                      }
                    }}
                    query={groupQuery}
                    langMode={langMode}
                    selectedProjectId={candidateProjectIds}
                    disabledGroupTooltip={t.groupDisabledNoMembers}
                  />
                )}
              </div>
            </div>
          ) : <p className="text-sm text-slate-600">{t.inventoryClearCustodyHint}</p>}
          </div>


        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-[#E5E7EB] px-6 py-4">
          <CommonButton
            colorIntent="danger"
            onClick={handleClose}
            variant="outline"
            theme={theme}
            disabled={saving}
            className={MODAL_FOOTER_BUTTON_CLASS}
            data-testid="inventory-transfer-cancel-button"
          >
            {t.cancel}
          </CommonButton>
          <CommonButton
            onClick={handleTransfer}
            variant="primary"
            theme={theme}
            disabled={
              saving ||
              hasBlockedItems ||
              (inputMode === "selected" && selectedItems.length === 0) ||
              (target === "group" && (groupsLoading || loadedGroupScope !== groupScope || !selectedGroup || !isGroupAssociatedWithProject(selectedGroup, candidateProjectIds)))
            }
            className={MODAL_FOOTER_BUTTON_CLASS}
            data-testid="inventory-transfer-confirm-button"
          >
            {saving ? (
              <span className="inline-flex items-center gap-2">
                <InlineSpinner size={14} />
                {t.inventoryTransferring}
              </span>
            ) : isReassign ? t.inventoryReassign : t.inventoryTransferConfirm}
          </CommonButton>
        </div>
      </div>
    </div>
  );
};

export default InventoryTransferModal;
